/** Analytics number/date formatting. YouTube's figures are shown as returned; only units change (minutes → hours, seconds → m:ss). */
import { clock } from "../../lib/format";

export const DASH = "—";
const whole = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });
const oneDp = new Intl.NumberFormat(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });

const ok = (n: number | null | undefined): n is number => typeof n === "number" && Number.isFinite(n);
export const count = (n: number | null | undefined) => (ok(n) ? whole.format(n) : DASH);
export const hours = (minutes: number | null | undefined) => (ok(minutes) ? oneDp.format(minutes / 60) : DASH);
export const duration = (seconds: number | null | undefined) => (ok(seconds) ? clock(seconds) : DASH);
export const percent = (n: number | null | undefined) => (ok(n) ? `${oneDp.format(n)} %` : DASH);

/** "2026-09-27" → "27 Sep 2026" (a calendar day: formatted in UTC so it never shifts a day). */
export function day(ymd: string | null | undefined): string {
  if (!ymd) return "";
  const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(ymd) ? `${ymd}T00:00:00Z` : ymd);
  return Number.isNaN(d.getTime()) ? ymd : d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
}

export function timeOfDay(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

export const RANGE_LABEL: Record<string, string> = { "7d": "Last 7 days", "28d": "Last 28 days", "90d": "Last 90 days", lifetime: "Lifetime" };

export const CONTENT_TYPE_LABEL: Record<string, string> = {
  SHORTS: "Shorts", VIDEO_ON_DEMAND: "Videos", LIVE_STREAM: "Live streams", STORY: "Stories", UNSPECIFIED: "Other",
};
export const contentTypeLabel = (t: string) => CONTENT_TYPE_LABEL[t.toUpperCase()] ?? t.charAt(0).toUpperCase() + t.slice(1).toLowerCase().replace(/_/g, " ");

export const PRIVACY_LABEL: Record<string, string> = { public: "Public", unlisted: "Unlisted", private: "Private" };
export const privacy = (p: string | null | undefined) => (p ? PRIVACY_LABEL[p] ?? p : DASH);

/** Params as compact key=value pairs (long values shortened; the full value stays in the title). */
export function paramPairs(params: Record<string, unknown> | null | undefined): { text: string; full: string }[] {
  return Object.entries(params ?? {}).filter(([k]) => k !== "dropped_metrics").map(([k, v]) => {
    const value = Array.isArray(v) ? v.join(",") : v !== null && typeof v === "object" ? JSON.stringify(v) : String(v);
    const full = `${k}=${value}`;
    return { full, text: value.length > 48 ? `${k}=${value.slice(0, 45)}…` : full };
  });
}
